import React, { useEffect, useState } from 'react'
import { useControl, Marker, MarkerProps, ControlPosition } from 'react-map-gl'
import MapboxGeocoder, { GeocoderOptions } from '@mapbox/mapbox-gl-geocoder'
import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson'
import { NullableString } from '@probatix/common/models/global'

type GeocoderControlProps = Omit<GeocoderOptions, 'accessToken' | 'mapboxgl' | 'marker'> & {
  address: NullableString;
  geoLocationList: FeatureCollection<Geometry, GeoJsonProperties>;
  mapboxAccessToken: string;
  marker?: boolean | Omit<MarkerProps, 'longitude' | 'latitude'>;
  onClear?: (e: object) => void;
  onError?: (e: object) => void;
  onLoading?: (e: object) => void;
  onResult?: (e: object) => void;
  onResults?: (e: object) => void;
  position: ControlPosition;
  query: NullableString,
  searchText: NullableString;
  selectedFeature?: any
}

/* eslint-disable */
export default function GeocoderControl(props: GeocoderControlProps) {
  const [marker, setMarker] = useState<any>(null)

  useEffect(() => {
    if (!!props.searchText) {
      geocoder.setInput(props.searchText!);
    }
  }, [props.searchText])


  const setFeature = (result) => {
    try {
      const location = result
        && (result.center || ('Point' === result.geometry?.type && result.geometry.coordinates))
      if (location && props.marker) {
        setMarker(
          <Marker
            latitude={location[1]}
            longitude={location[0]}
            color='red'
          />)
      } else {
        setMarker(null)
      }
    } catch (error) {
      setMarker(null)
    }
  }

  useEffect(() => {
    if (!!props.selectedFeature) {
      geocoder.query(props.selectedFeature.place_name).setFlyTo({
        animate: true,
        zoom: 12,
        center: [
          props.selectedFeature.center[0].toString(),
          props.selectedFeature.center[1].toString(),
        ],
      });
      setFeature(props.selectedFeature)

    }
  }, [props.selectedFeature])

  const geocoder = useControl<MapboxGeocoder>(
    () => {
      const ctrl = new MapboxGeocoder({
        ...props,
        accessToken: props.mapboxAccessToken,
        marker: true,
        placeholder: props.placeholder,
        countries: "de",
        // bbox: [13.0883, 52.3383, 13.7611, 52.6755], // only berlin 
      })
      ctrl.on('loading', props.onLoading!)
      ctrl.on('results', props.onResults!)
      ctrl.on('result', (evt) => {
        const { result } = evt
        setFeature(result)
      })
      ctrl.on('error', props.onError!)

      return ctrl
    },
    {
      position: props.position,
    },
  )

  // @ts-ignore (TS2339) private member
  if (geocoder._map) {
    if (geocoder.getProximity() !== props.proximity && props.proximity !== undefined) {
      geocoder.setProximity(props.proximity)
    }
    if (geocoder.getRenderFunction() !== props.render && props.render !== undefined) {
      geocoder.setRenderFunction(props.render)
    }
    if (geocoder.getLanguage() !== props.language && props.language !== undefined) {
      geocoder.setLanguage(props.language)
    }
    if (geocoder.getZoom() !== props.zoom && props.zoom !== undefined) {
      geocoder.setZoom(props.zoom)
    }
    if (geocoder.getFlyTo() !== props.flyTo && props.flyTo !== undefined) {
      geocoder.setFlyTo(props.flyTo)
    }
    if (geocoder.getPlaceholder() !== props.placeholder && props.placeholder !== undefined) {
      geocoder.setPlaceholder(props.placeholder)
    }
    if (geocoder.getCountries() !== props.countries && props.countries !== undefined) {
      geocoder.setCountries(props.countries)
    }
    if (geocoder.getTypes() !== props.types && props.types !== undefined) {
      geocoder.setTypes(props.types)
    }
    if (geocoder.getMinLength() !== props.minLength && props.minLength !== undefined) {
      geocoder.setMinLength(props.minLength)
    }
    if (geocoder.getLimit() !== props.limit && props.limit !== undefined) {
      geocoder.setLimit(props.limit)
    }
    if (geocoder.getFilter() !== props.filter && props.filter !== undefined) {
      geocoder.setFilter(props.filter)
    }
    if (geocoder.getOrigin() !== props.origin && props.origin !== undefined) {
      geocoder.setOrigin(props.origin)
    }
    // Types missing from @types/mapbox__mapbox-gl-geocoder
    // if (geocoder.getAutocomplete() !== props.autocomplete && props.autocomplete !== undefined) {
    //   geocoder.setAutocomplete(props.autocomplete);
    // }
    // if (geocoder.getFuzzyMatch() !== props.fuzzyMatch && props.fuzzyMatch !== undefined) {
    //   geocoder.setFuzzyMatch(props.fuzzyMatch);
    // }
    // if (geocoder.getRouting() !== props.routing && props.routing !== undefined) {
    //   geocoder.setRouting(props.routing);
    // }
    // if (geocoder.getWorldview() !== props.worldview && props.worldview !== undefined) {
    //   geocoder.setWorldview(props.worldview);
    // }
  }

  return marker
}

const noop = () => { }

GeocoderControl.defaultProps = {
  marker: true,
  onClose: noop,
  onError: noop,
  onLoading: noop,
  onResult: noop,
  onResults: noop,
}
